import React, { useState, useRef } from 'react';
import { Play, Pause, Headphones } from 'lucide-react';
import styles from './LessonContentRenderer.module.css';

interface AudioPlayerProps {
    url: string;
    duration: string;
    context?: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ url, duration, context }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [useFallback, setUseFallback] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const speechRef = useRef<SpeechSynthesisUtterance | null>(null);

    // Stop all audio on unmount
    React.useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
            }
            window.speechSynthesis.cancel();
        };
    }, []);

    const togglePlay = () => {
        if (useFallback) {
            handleFallbackToggle();
            return;
        }

        if (!audioRef.current) return;

        if (isPlaying) {
            audioRef.current.pause();
            setIsPlaying(false);
        } else {
            // Check if source is valid or if there's an error already
            if (audioRef.current.error) {
                setUseFallback(true);
                startAIInternal();
            } else {
                audioRef.current.play().catch((err) => {
                    console.warn('Audio play failed, switching to fallback:', err);
                    setUseFallback(true);
                    startAIInternal();
                });
                setIsPlaying(true);
            }
        }
    };

    const startAIInternal = () => {
        window.speechSynthesis.cancel(); // Stop any current speech

        const textToSpeak = `Summary for ${context || 'this lesson'}. This is a quick recap of the key points covered in the section above. Driving safely requires constant attention and understanding of the rules of the road.`;

        const utterance = new SpeechSynthesisUtterance(textToSpeak);
        utterance.rate = 0.9; // Slightly slower for clarity
        utterance.pitch = 1.0;

        utterance.onend = () => setIsPlaying(false);
        utterance.onerror = () => {
            setIsPlaying(false);
            setUseFallback(false);
        };

        speechRef.current = utterance;
        window.speechSynthesis.speak(utterance);
        setIsPlaying(true);
    };

    const handleFallbackToggle = () => {
        if (isPlaying) {
            window.speechSynthesis.cancel();
            setIsPlaying(false);
        } else {
            startAIInternal();
        }
    };

    return (
        <div className={styles.audioPlayerContainer}>
            <div className={`${styles.audioPlayerIcon} ${useFallback ? styles.aiIcon : ''}`}>
                <Headphones size={24} />
            </div>
            <div className={styles.audioPlayerContent}>
                <div className={styles.audioPlayerTitle}>
                    Quick-Listen {useFallback ? '(AI Narration)' : 'Lesson TL;DR'}
                </div>
                <div className={styles.audioPlayerMeta}>
                    {useFallback ? 'Generated on-the-fly' : `Duration: ${duration}`}
                </div>
            </div>
            <button
                onClick={togglePlay}
                className={`${styles.audioPlayerButton} ${isPlaying ? styles.isPlaying : ''}`}
                aria-label={isPlaying ? 'Pause narration' : 'Play narration'}
            >
                {isPlaying ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" />}
            </button>
            <audio
                ref={audioRef}
                src={url}
                onEnded={() => setIsPlaying(false)}
                onError={() => {
                    if (!useFallback) {
                        setUseFallback(true);
                        if (isPlaying) {
                            setIsPlaying(false);
                            startAIInternal();
                        }
                    }
                }}
                hidden
            />
        </div>
    );
};


export default AudioPlayer;
